import { useState } from 'react'
import type { OfferType } from '../../types/offer'
import { createOffer } from '../../services/offerService'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { X } from 'lucide-react'

interface CreateOfferModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
}

export function CreateOfferModal({ isOpen, onClose, onCreated }: CreateOfferModalProps) {
  const [type, setType] = useState<OfferType>('Sell')
  const [title, setTitle] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await createOffer({
        type,
        title: title.trim() || null,
        status: 'active',
      })
      setTitle('')
      setType('Sell')
      onCreated()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create offer')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null)
      setTitle('')
      setType('Sell')
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={handleBackdropClick}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-semibold text-gray-900 m-0">Create Offer</h3>
          <Button variant="ghost" size="icon" onClick={handleClose} disabled={isSubmitting} className="text-gray-600 hover:text-gray-900 rounded-md" aria-label="Close modal">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="offer-type" className="text-gray-700">
              Type
            </Label>
            <Select value={type} onValueChange={(v) => setType(v as OfferType)}>
              <SelectTrigger id="offer-type" className="mt-1.5 bg-white border-gray-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Sell">
                  <span className="text-gray-900 bg-white">Sell</span>
                </SelectItem>
                <SelectItem value="Buy">
                  <span className="text-gray-900 bg-white">Buy</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="offer-title" className="text-gray-700">
              Title <span className="text-gray-500 font-normal">(optional)</span>
            </Label>
            <Input
              id="offer-title"
              type="text"
              placeholder="e.g. Weekend sale"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5 bg-white border-gray-300"
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <div className="flex gap-3 mt-6">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting} className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1 bg-blue-800 text-white hover:bg-blue-900">
              {isSubmitting ? 'Creating…' : 'Create Offer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
